import { useEffect, useMemo } from 'react'
import { InjectedSoundProps, withSoundEffectsContext } from '@astrid/components'
import { debounce } from 'throttle-debounce'
import useEnhancedState from 'hooks/useEnhancedState'
import { Book, BookMode, BookProgress, CEFRLevels, GESELevels } from 'store/services/Book'
import { SimpleWordScore } from 'store/services/Exercise'
import { StudyBuddyMood, WordScore } from 'utils/types'

import { asyncGetReadingScore } from './helpers'
import WordsPracticeReading from './WordsPracticeReading/WordsPracticeReading'
import WordsPracticeAnimaatedProgressBar from './WordsPracticeAnimatedProgressBar/WordsPracticeAnimatedProgressBar'
import WordsPracticePreview from './WordsPracticePreview/WordsPracticePreview'
import WordsPracticeSummary from './WordsPracticeSummary/WordsPracticeSummary'

import './WordsPractice.scss'
import styles from './WordsPractice.module.scss'

interface WordsPracticeProps extends InjectedSoundProps {
  words: WordScore[] | SimpleWordScore[]
  numberOfWordsToPractice: number
  onFinishPractice: () => void
  practiceSetType?: string
  isPracticePage?: boolean
  isReadingLevelUp?: boolean
  studySetId?: string
  bookData?: {
    book: Book
    bookProgress: BookProgress
    bookMode: BookMode
  }
}

interface BookPracticeState {
  wordsData: { wordScore: WordScore | null; attemps: number }[]
  mood: StudyBuddyMood
  hasBeenContinued: boolean
  hasFinishedAllWords: boolean
  activeWordIndex: number
  isSuccess: boolean
  isGettingScore: boolean
  showConfetti: boolean
  scoresComparison: Array<{
    word: string
    was: number
    is: number
  }>
}

const WordsPractice: React.FC<WordsPracticeProps> = ({
  words,
  numberOfWordsToPractice,
  bookData,
  onFinishPractice,
  isPracticePage,
  isReadingLevelUp,
  studySetId,
  playSound
}) => {
  const _words = useMemo(() => words.slice(0, numberOfWordsToPractice) || [], [words, numberOfWordsToPractice])

  const [state, setState] = useEnhancedState<BookPracticeState>({
    activeWordIndex: 0,
    hasBeenContinued: false,
    hasFinishedAllWords: false,
    mood: StudyBuddyMood.Correct,
    isGettingScore: false,
    isSuccess: false,
    wordsData: _words.map(() => ({ wordScore: null, attemps: 0 })),
    showConfetti: false,
    scoresComparison: _words.map(({ score, word }) => ({ was: score, is: 0, word }))
  })

  const goToNextWord = debounce(200, () => {
    if (state.activeWordIndex === _words.length - 1) {
      setState({
        hasFinishedAllWords: true
      })

      return
    }

    setState((s) => ({
      ...s,
      activeWordIndex: s.activeWordIndex + 1,
      isGettingScore: false,
      isSuccess: false,
      showConfetti: false
    }))
  })

  // [feature/pronunciation-pro]
  // const goToPreviousWord = debounce(200, () => {
  //   if (state.activeWordIndex <= 0) return
  //
  //   setState((s) => ({
  //     ...s,
  //     activeWordIndex: s.activeWordIndex - 1,
  //     isGettingScore: false,
  //     isSuccess: false,
  //     showConfetti: false
  //   }))
  // })
  //
  // const renderHeaderBackBtn = useCallback(() => {
  //   const buttonStyleWithMarginLeft: StyleProp<ViewStyle> = { marginLeft: 10 }
  //   const shouldShowBackArrow = state.activeWordIndex > 0 && state.activeWordIndex < _words.length
  //   return shouldShowBackArrow ? (
  //     <HeaderBackButton style={buttonStyleWithMarginLeft} onPress={goToPreviousWord} icon="chevron-left" />
  //   ) : null
  // }, [state.activeWordIndex, _words.length, goToPreviousWord])

  const asyncGetScore = async (path: Blob) => {
    if (_words.length > 0) {
      try {
        setState({ isGettingScore: true })
        const { wordScores } = await asyncGetReadingScore(
          bookData && bookData.book ? bookData.book._id : '',
          path,
          _words[state.activeWordIndex].word,
          { isPracticePage, isReadingLevelUp, studySetId }
        )

        const wordScore = wordScores[0]

        if (wordScore.score >= 80) {
          // [feature/pronunciation-pro]: wordScore.score === 100 ? "celebration"
          playSound('correct')

          setState((s) => ({
            ...s,
            wordsData: s.wordsData.map((data, index) =>
              index === state.activeWordIndex ? { wordScore, attemps: data.attemps + 1 } : data
            ),
            isSuccess: true,
            showConfetti: wordScore.score === 100,
            scoresComparison: s.scoresComparison.map((comp, i) => {
              if (s.activeWordIndex === i) {
                return {
                  ...comp,
                  is: wordScore.score > comp.is ? wordScore.score : comp.is
                }
              } else {
                return comp
              }
            })
          }))
        } else {
          playSound('incorrect')
          setState((s) => ({
            ...s,
            wordsData: s.wordsData.map((data, index) =>
              index === state.activeWordIndex ? { wordScore, attemps: data.attemps + 1 } : data
            ),
            scoresComparison: s.scoresComparison.map((comp, i) => {
              if (s.activeWordIndex === i) {
                return {
                  ...comp,
                  is: wordScore.score > comp.is ? wordScore.score : comp.is
                }
              } else {
                return comp
              }
            })
          }))
        }
      } catch (error) {
      } finally {
        setState({ isGettingScore: false })
      }
    }
  }

  const onStartClick = () => {
    setState({ hasBeenContinued: true })
  }

  useEffect(() => {
    const timeout = setTimeout(() => {
      setState({ mood: StudyBuddyMood.Idle })
    }, 2000)

    return () => {
      clearTimeout(timeout)
    }
  }, [])

  return (
    <div className={styles.container}>
      {state.hasFinishedAllWords ? (
        <WordsPracticeSummary
          bookData={
            bookData && {
              cefrLevel: bookData.book.cefrLevel || CEFRLevels.A11,
              geseLevel: bookData.book.geseLevel || GESELevels.ONE,
              title: bookData.book.title,
              points: bookData.book.totalPoints
            }
          }
          onFinish={onFinishPractice}
          scoresComparison={state.scoresComparison}
        />
      ) : state.hasBeenContinued ? (
        <>
          <WordsPracticeAnimaatedProgressBar itemsCount={_words.length} activeItemIndex={state.activeWordIndex + 1} />
          <div className={styles.slider}>
            <div
              className={styles.sliderTrack}
              style={{
                transform: `translateX(-${state.activeWordIndex * 100}%)`
              }}
            >
              {_words.map((item, index) => (
                <div key={item.word} className={styles.slide}>
                  <WordsPracticeReading
                    word={item.word}
                    attempts={state.wordsData[index].attemps}
                    index={index}
                    isGeneratingScore={state.isGettingScore}
                    isSuccess={state.isSuccess}
                    wordScore={state.wordsData[index].wordScore}
                    numberOfWords={_words.length}
                    getScore={asyncGetScore}
                    goToNextWord={goToNextWord}
                  />
                </div>
              ))}
            </div>
          </div>
        </>
      ) : (
        <WordsPracticePreview mood={state.mood} onStartClick={onStartClick} words={_words} isRegularProPro={true} />
      )}
    </div>
  )
}

export default withSoundEffectsContext(WordsPractice)
